import { useState, useCallback, useEffect, useRef } from 'react';
import api, { MediaAttachment } from '@/lib/api';

/**
 * Manages media file uploads for social media posts.
 *
 * Upload flow for new files:
 * 1. Create blob URL for immediate local preview
 * 2. POST /files/upload-url  → presigned S3 URL + fileId + s3Key
 * 3. PUT binary to S3 presigned URL
 * 4. POST /files/:fileId/confirm → persist metadata record
 * 5. Replace temp ID with real fileId in state
 *
 * For existing fileIds (editing a saved post):
 * - Initialises placeholders immediately so the list renders
 * - Fetches metadata + presigned download URL in the background and fills
 *   in fileName, mimeType, sizeBytes, and previewUrl once resolved
 */
export function useMediaUpload(initialFileIds: string[] = []) {
  // Capture initialFileIds at mount time so the effect closure is stable
  const initialFileIdsRef = useRef(initialFileIds);

  const [attachments, setAttachments] = useState<MediaAttachment[]>(() =>
    initialFileIds.map((fileId) => ({
      fileId,
      fileName: '',
      mimeType: 'application/octet-stream',
      previewUrl: '',
      sizeBytes: 0,
      uploadState: 'done' as const,
    })),
  );

  // On mount, resolve metadata + presigned read URLs for pre-existing files
  useEffect(() => {
    const fileIds = initialFileIdsRef.current;
    if (fileIds.length === 0) return;

    let cancelled = false;

    Promise.all(
      fileIds.map(async (fileId) => {
        try {
          const [{ file }, { downloadUrl }] = await Promise.all([
            api.getFile(fileId),
            api.getDownloadUrl(fileId),
          ]);
          return {
            fileId,
            fileName: file.fileName,
            mimeType: file.mimeType ?? 'application/octet-stream',
            previewUrl: downloadUrl,
            sizeBytes: file.sizeBytes,
            uploadState: 'done' as const,
          } satisfies MediaAttachment;
        } catch {
          // Keep the placeholder — file may have been deleted or access denied
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      setAttachments((prev) =>
        prev.map((a) => {
          const fetched = results.find((r) => r?.fileId === a.fileId);
          return fetched ?? a;
        }),
      );
    });

    return () => {
      cancelled = true;
    };
  }, []); // intentionally empty — only runs at mount with the captured ref

  const uploadFile = useCallback(async (file: File): Promise<void> => {
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const previewUrl = URL.createObjectURL(file);

    // Register placeholder so the UI shows the preview immediately
    setAttachments((prev) => [
      ...prev,
      {
        fileId: tempId,
        fileName: file.name,
        mimeType: file.type,
        previewUrl,
        sizeBytes: file.size,
        uploadState: 'uploading',
      },
    ]);

    try {
      const memberships = await api.listMyVaultMemberships();
      if (!memberships.personalHolder) {
        throw new Error('Personal workspace not provisioned');
      }
      const doc = await api.uploadFileToVault(file, memberships.personalHolder.holderId, {
        tags: ['social-media'],
      });
      // Promote the placeholder to a confirmed attachment
      setAttachments((prev) =>
        prev.map((a) =>
          a.fileId === tempId ? { ...a, fileId: doc.documentId, uploadState: 'done' } : a,
        ),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      setAttachments((prev) =>
        prev.map((a) =>
          a.fileId === tempId ? { ...a, uploadState: 'error', errorMessage: message } : a,
        ),
      );
    }
  }, []);

  const removeAttachment = useCallback((fileId: string) => {
    setAttachments((prev) => {
      const found = prev.find((a) => a.fileId === fileId);
      if (found?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(found.previewUrl);
      }
      return prev.filter((a) => a.fileId !== fileId);
    });
  }, []);

  const retryUpload = useCallback(
    async (fileId: string, file: File) => {
      removeAttachment(fileId);
      await uploadFile(file);
    },
    [removeAttachment, uploadFile],
  );

  /**
   * Add files that already exist in the file storage system (no upload needed).
   * Fetches metadata + presigned download URL for each file and adds them as
   * confirmed attachments.
   */
  const addExistingFiles = useCallback(
    async (
      files: Array<{
        fileId: string;
        fileName: string;
        mimeType: string | null;
        sizeBytes: number;
        /** Optional pre-resolved preview URL (e.g. from AI image generation) */
        previewUrl?: string;
      }>,
    ): Promise<void> => {
      // Add placeholders immediately so the UI updates
      const newAttachments: MediaAttachment[] = files
        .filter((f) => !attachments.some((a) => a.fileId === f.fileId))
        .map((f) => ({
          fileId: f.fileId,
          fileName: f.fileName,
          mimeType: f.mimeType ?? 'application/octet-stream',
          previewUrl: f.previewUrl ?? '',
          sizeBytes: f.sizeBytes,
          uploadState: 'done' as const,
        }));

      if (newAttachments.length === 0) return;

      setAttachments((prev) => [...prev, ...newAttachments]);

      // Fetch presigned download URLs for attachments that don't already have one
      const needsUrl = newAttachments.filter((a) => !a.previewUrl);
      if (needsUrl.length === 0) return;

      const results = await Promise.all(
        needsUrl.map(async (a) => {
          try {
            const { downloadUrl } = await api.getDownloadUrl(a.fileId);
            return { fileId: a.fileId, previewUrl: downloadUrl };
          } catch {
            return { fileId: a.fileId, previewUrl: '' };
          }
        }),
      );

      setAttachments((prev) =>
        prev.map((a) => {
          const resolved = results.find((r) => r.fileId === a.fileId);
          return resolved && !a.previewUrl ? { ...a, previewUrl: resolved.previewUrl } : a;
        }),
      );
    },
    [attachments],
  );

  /** fileIds of confirmed (fully uploaded) attachments — pass these to the post API */
  const confirmedFileIds = attachments.filter((a) => a.uploadState === 'done').map((a) => a.fileId);

  const hasUploading = attachments.some((a) => a.uploadState === 'uploading');

  return {
    attachments,
    setAttachments,
    uploadFile,
    removeAttachment,
    retryUpload,
    addExistingFiles,
    confirmedFileIds,
    hasUploading,
  };
}
